import { useCallback, useEffect, useState } from 'react';
import { AtSign, X } from 'lucide-react';
import { Avatar } from '@/components/apps/Avatar';
import { Button } from '@/components/ui/Button';
import { supabase } from '@/lib/supabase';
import type { AppMember } from '@/lib/app-records';
import { type AppComment, loadRecordComments, postRecordComment } from '@/lib/app-collaboration';

interface AppRecordCommentsProps {
  appId: string;
  recordId: string;
  members: AppMember[];
  teams: Array<{ id: string; name: string }>;
  /** Called after a comment is posted (the history gains a line). */
  onPosted?: () => void;
}

type Mention = { kind: 'user' | 'team'; id: string; name: string };

/** Comments on an object, visible to everyone in the app; mention a person or a team. */
export const AppRecordComments = ({ appId, recordId, members, teams, onPosted }: AppRecordCommentsProps) => {
  const [comments, setComments] = useState<AppComment[]>([]);
  const [body, setBody] = useState('');
  const [mentions, setMentions] = useState<Mention[]>([]);
  const [picking, setPicking] = useState(false);
  const [posting, setPosting] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const nameOf = (userId: string) => members.find((member) => member.userId === userId)?.name ?? 'Quelqu’un';
  const mentionName = (mention: { kind: 'user' | 'team'; id: string }) =>
    mention.kind === 'user' ? nameOf(mention.id) : teams.find((team) => team.id === mention.id)?.name ?? 'Équipe';

  const reload = useCallback(() => {
    loadRecordComments(appId, recordId).then(setComments).catch((cause) => setError(cause instanceof Error ? cause.message : 'Commentaires indisponibles.'));
  }, [appId, recordId]);

  useEffect(() => {
    reload();
    const channel = supabase.channel(`app-record-comments-${recordId}`)
      .on('postgres_changes', { event: 'INSERT', schema: 'public', table: 'tool_messages' }, reload)
      .subscribe();
    return () => { void supabase.removeChannel(channel); };
  }, [recordId, reload]);

  const post = async () => {
    if (!body.trim()) return;
    setPosting(true);
    setError(null);
    try {
      await postRecordComment(recordId, body.trim(), mentions);
      setBody('');
      setMentions([]);
      reload();
      onPosted?.();
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : 'Le commentaire n’a pas pu être publié.');
    } finally {
      setPosting(false);
    }
  };

  const options: Mention[] = [
    ...teams.map((team) => ({ kind: 'team' as const, id: team.id, name: team.name })),
    ...members.map((member) => ({ kind: 'user' as const, id: member.userId, name: member.name })),
  ].filter((option) => !mentions.some((mention) => mention.kind === option.kind && mention.id === option.id));

  return (
    <div>
      <ol className="space-y-3">
        {comments.map((comment) => (
          <li key={comment.id} className="flex gap-2">
            <Avatar name={nameOf(comment.authorUserId)} />
            <div className="min-w-0 flex-1 rounded-xl bg-domino-warm px-3 py-2">
              <p className="text-xs text-domino-inkSoft">
                <span className="font-medium text-domino-ink">{nameOf(comment.authorUserId)}</span>
                {' · '}{new Date(comment.createdAt).toLocaleString('fr-FR', { day: 'numeric', month: 'short', hour: '2-digit', minute: '2-digit' })}
              </p>
              <p className="mt-0.5 whitespace-pre-wrap text-sm leading-6 text-domino-ink">{comment.body}</p>
              {comment.mentions.length > 0 && (
                <p className="mt-1 text-xs text-domino-aubergine">{comment.mentions.map((mention) => `@${mentionName(mention)}`).join(' ')}</p>
              )}
            </div>
          </li>
        ))}
      </ol>
      <div className="mt-3 rounded-xl border border-domino-cardBorder p-2">
        <textarea value={body} onChange={(event) => setBody(event.target.value)} rows={2} maxLength={4000}
          placeholder="Écrire un commentaire…" aria-label="Commentaire"
          className="w-full resize-y border-0 bg-transparent text-sm text-domino-ink placeholder:text-domino-inkSoft focus:outline-none" />
        <div className="flex flex-wrap items-center gap-1.5">
          {mentions.map((mention) => (
            <span key={`${mention.kind}:${mention.id}`} className="inline-flex items-center gap-1 rounded-full bg-domino-lilac px-2 py-0.5 text-xs text-domino-aubergine">
              @{mention.name}
              <button type="button" aria-label={`Retirer ${mention.name}`} onClick={() => setMentions(mentions.filter((item) => item !== mention))}><X className="h-3 w-3" /></button>
            </span>
          ))}
          {picking ? (
            <select autoFocus aria-label="Mentionner" defaultValue="" onBlur={() => setPicking(false)}
              onChange={(event) => {
                const option = options.find((item) => `${item.kind}:${item.id}` === event.target.value);
                if (option) setMentions([...mentions, option]);
                setPicking(false);
              }} className="rounded-lg border border-domino-cardBorder px-2 py-0.5 text-xs">
              <option value="" disabled>Mentionner…</option>
              {options.map((option) => <option key={`${option.kind}:${option.id}`} value={`${option.kind}:${option.id}`}>{option.kind === 'team' ? `Équipe ${option.name}` : option.name}</option>)}
            </select>
          ) : (
            <button type="button" onClick={() => setPicking(true)} className="inline-flex items-center gap-1 rounded-lg px-2 py-0.5 text-xs text-domino-inkSoft hover:bg-domino-muted">
              <AtSign className="h-3.5 w-3.5" aria-hidden="true" />Mentionner
            </button>
          )}
          <Button className="ml-auto" onClick={() => void post()} disabled={posting || !body.trim()}>{posting ? 'Publication…' : 'Publier'}</Button>
        </div>
      </div>
      {error && <p role="alert" className="mt-2 text-sm text-[var(--domino-critical)]">{error}</p>}
    </div>
  );
};
